/**
 * Simple / Pro 로 바꿔 쓰는 설명글 편집기.
 *
 * Simple — 공용 리치 텍스트(richText.js). 매장 페이지가 그리는 서식만 쓴다.
 * Pro    — 워드프레스 편집기(TinyMCE + 코드 탭, wp.editor). 문단 형식 · 번호 목록 · 인용 ·
 *          정렬 · 링크 등을 쓸 수 있지만 매장 페이지 모양이 보장되지 않아 아래에 경고를 띄운다.
 *          편집기 스크립트는 처음 필요할 때(Pro 로 열리는 글이 있거나 Pro 를 누를 때) 한 번 받는다 —
 *          서버가 심어 둔 window.qrshopProEditorUrl(Shop_Setup::handle_pro_editor)이 관리 화면 푸터와
 *          같은 태그 · 설정 조각을 내려 주고, loadProEditor 가 그 태그를 순서대로 실행한다.
 *
 * 모드 전환 버튼(switcher)은 라벨 줄 오른쪽에 두라고 따로 돌려준다.
 * TinyMCE 는 문서에 붙은 textarea 에만 뜨므로 element 를 화면에 넣은 뒤 mount() 를 부른다.
 * 모달처럼 한 편집기에 글을 바꿔 넣을 때는 setContent(html, mode) — 다음 프레임에 알아서 띄운다.
 * 카드를 다시 그리기 전에는 destroy() 로 편집기 인스턴스를 치운다.
 *
 * 모달(<dialog>) 안이면 TinyMCE 드롭다운(문단 형식 · 색)과 링크 창을 dialog 안에 그린다(keepLinkDialogInModal).
 * dialog 는 최상위 레이어라 body 에 붙는 창은 모달 뒤에 가려진다.
 */

import { createRichText, sanitizeHTML } from './richText';

const MODE_SIMPLE = 'simple';
const MODE_PRO = 'pro';

// Simple 편집기가 그대로 다룰 수 있는 태그. 이것 말고 다른 태그나 style · href 같은 속성이 있으면
// Pro → Simple 전환 때 사라진다
// (다른 단계의 제목은 richText 가 타이틀 h3 으로 맞춘다)
const SIMPLE_TAGS = [
  'P',
  'BR',
  'H1',
  'H2',
  'H3',
  'H4',
  'H5',
  'H6',
  'STRONG',
  'B',
  'EM',
  'I',
  'S',
  'STRIKE',
  'DEL',
  'UL',
  'OL',
  'LI',
  'HR',
  'SPAN',
];

const PRO_TOOLBAR1 =
  'formatselect,bold,italic,bullist,numlist,blockquote,alignleft,aligncenter,alignright,link,wp_adv';
const PRO_TOOLBAR2 =
  'strikethrough,hr,forecolor,pastetext,removeformat,charmap,outdent,indent,undo,redo';

// Pro 편집 화면 안(iframe) 글자 · 구분선 — Simple 편집기와 같은 결로 맞춘다 (구분선 위아래 16px 은 매장 페이지와 같다)
const PRO_CONTENT_STYLE = [
  'body{font-family:-apple-system,BlinkMacSystemFont,"Apple SD Gothic Neo","Noto Sans KR",sans-serif;font-size:14px;line-height:1.625;color:#374151}',
  'hr{margin:16px 0;border:0;border-top:1px solid #e5e7eb}',
].join('');

// 미리보기에 넣기 전 걸러 낼 것 — 저장 때는 서버가 wp_kses_post 로 다시 거른다
const DROPPED = ['SCRIPT', 'STYLE', 'IFRAME', 'OBJECT', 'EMBED', 'NOSCRIPT', 'TEMPLATE', 'FORM'];

/** Pro HTML 을 화면(요약 미리보기)에 넣을 수 있게 스크립트 · 이벤트 속성 · javascript: 주소를 뗀다. */
const sanitizeProHTML = (html) => {
  const holder = document.createElement('div');
  holder.innerHTML = html || '';

  holder.querySelectorAll('*').forEach((element) => {
    if (DROPPED.includes(element.tagName)) {
      element.remove();
      return;
    }

    Array.from(element.attributes).forEach((attribute) => {
      const name = attribute.name.toLowerCase();
      const unsafeUrl = ['href', 'src'].includes(name) && /^\s*javascript:/i.test(attribute.value);

      if (name.startsWith('on') || unsafeUrl) {
        element.removeAttribute(attribute.name);
      }
    });
  });

  return holder.innerHTML.trim();
};

/** Simple 로 바꾸면 사라질 서식이 있는지 */
const hasProOnlyMarkup = (html) => {
  const holder = document.createElement('div');
  holder.innerHTML = html || '';

  return Array.from(holder.querySelectorAll('*')).some(
    (element) =>
      !SIMPLE_TAGS.includes(element.tagName) ||
      Array.from(element.attributes).some((attribute) => 'class' !== attribute.name)
  );
};

/** 글자도 구분선 · 사진도 없으면 빈 값 */
const isBlankHTML = (html) => {
  const holder = document.createElement('div');
  holder.innerHTML = html || '';

  return (
    '' === holder.textContent.replace(/[\s\u00A0\u200B]/g, '') &&
    !holder.querySelector('hr, img, li')
  );
};

let sequence = 0;
let linkDialogWatched = false;

/**
 * 링크 넣기 창(wpLink)을 편집기가 든 모달 안으로 옮긴다.
 * wpLink.open() 은 열 때마다 창을 body 맨 끝으로 다시 붙이는데, 모달(<dialog>)이 열려 있으면
 * body 는 모달 뒤(최상위 레이어 밖)라 창이 뒤에 깔리고 누를 수도 없다. 열린 직후 오는
 * jQuery 'wplink-open' 이벤트에서 되돌려 놓는다. 페이지(모달 밖) 편집기면 그대로 둔다.
 */
const keepLinkDialogInModal = () => {
  if (linkDialogWatched || !window.jQuery) {
    return;
  }

  linkDialogWatched = true;

  window.jQuery(document).on('wplink-open', () => {
    const textarea = window.wpActiveEditor ? document.getElementById(window.wpActiveEditor) : null;
    const dialog = textarea?.closest('dialog[open]');

    if (!dialog) {
      return;
    }

    ['wp-link-backdrop', 'wp-link-wrap'].forEach((linkId) => {
      const node = document.getElementById(linkId);

      if (node && node.parentElement !== dialog) {
        dialog.append(node);
      }
    });
  });
};

/** 워드프레스 편집기가 이미 있는지 (조각을 받아 실행한 뒤 true) */
const proReady = () => Boolean(window.wp?.editor?.initialize);

/** 조각의 태그 하나를 문서에 넣는다. src 스크립트 · 스타일은 다 받을 때까지 기다린다 — 다음 인라인 설정이 그것을 쓴다. */
const runFragmentNode = (source) =>
  new Promise((resolve, reject) => {
    const isScript = 'SCRIPT' === source.tagName;
    const node = isScript ? document.createElement('script') : source;

    if (isScript) {
      Array.from(source.attributes).forEach((attribute) =>
        node.setAttribute(attribute.name, attribute.value)
      );
      node.textContent = source.textContent;
      node.async = false;
    }

    const waits = isScript
      ? source.hasAttribute('src')
      : 'LINK' === source.tagName && source.hasAttribute('href');

    if (waits) {
      node.addEventListener('load', () => resolve());
      node.addEventListener('error', () =>
        reject(
          new Error(
            `Pro 편집기 파일을 받지 못했습니다: ${source.getAttribute('src') || source.getAttribute('href')}`
          )
        )
      );
    }

    // 스크립트 · 스타일은 head, 링크 넣기 창(wpLink) 같은 마크업은 body
    (isScript || ['LINK', 'STYLE'].includes(source.tagName) ? document.head : document.body).append(
      node
    );

    if (!waits) {
      resolve();
    }
  });

let proLoading = null;

/**
 * 워드프레스 편집기 스크립트를 한 번만 받는다. 같은 화면의 편집기 여럿이 동시에 불러도 요청은 하나.
 * 실패하면 다음 호출이 다시 시도한다.
 *
 * @returns {Promise<boolean>} 편집기를 쓸 수 있으면 true. 주소가 없는 화면(로그인 전)이면 false.
 */
const loadProEditor = () => {
  if (proReady()) {
    return Promise.resolve(true);
  }

  if (proLoading) {
    return proLoading;
  }

  const url = window.qrshopProEditorUrl;

  if (!url) {
    return Promise.resolve(false);
  }

  proLoading = (async () => {
    const response = await fetch(url, { credentials: 'same-origin' });

    if (!response.ok) {
      throw new Error(`Pro 편집기 조각을 받지 못했습니다 (HTTP ${response.status})`);
    }

    const template = document.createElement('template');
    template.innerHTML = await response.text();

    // 태그를 한 번에 붙이면 인라인 설정이 아직 안 받은 스크립트(jQuery · tinymce)를 찾다 죽는다 — 하나씩
    for (const node of Array.from(template.content.children)) {
      // eslint-disable-next-line no-await-in-loop
      await runFragmentNode(node);
    }

    if (!proReady()) {
      throw new Error('Pro 편집기 조각에 wp.editor 가 없습니다');
    }

    return true;
  })().catch((error) => {
    proLoading = null;
    throw error;
  });

  return proLoading;
};

/**
 * @param {{ html?: string, mode?: string, placeholder?: string }} options
 */
const createModeEditor = ({ html = '', mode = MODE_SIMPLE, placeholder = '' } = {}) => {
  sequence += 1;
  const id = `qrshop-pro-editor-${sequence}`;

  let current = MODE_PRO === mode ? MODE_PRO : MODE_SIMPLE;
  let proStarted = false;
  let pendingProHTML = MODE_PRO === current ? html : '';
  // 편집기를 받는 동안 모드를 바꾸거나 카드를 다시 그리면 늦게 도착한 초기화를 버린다
  let proAttempt = 0;

  const element = document.createElement('div');
  element.className = 'mode-editor';

  const simple = createRichText({
    html: MODE_SIMPLE === current ? html : '',
    placeholder,
  });

  const proHolder = document.createElement('div');
  proHolder.className = 'mode-editor__pro';

  // 매장 페이지 모양이 보장되지 않는다는 경고 — Pro 일 때만 보인다
  const warning = document.createElement('div');
  warning.className = 'alert mode-editor__warning';
  warning.dataset.state = 'warning';
  warning.setAttribute('role', 'note');

  const warningIcon = document.createElement('span');
  warningIcon.className = 'alert__icon';
  warningIcon.setAttribute('aria-hidden', 'true');

  const warningBody = document.createElement('div');
  warningBody.className = 'alert__body';

  const warningTitle = document.createElement('p');
  warningTitle.className = 'alert__title';
  warningTitle.textContent = 'Pro 모드로 수정한 내용은 꼭 매장 사이트에서 확인하세요';

  const warningText = document.createElement('p');
  warningText.className = 'alert__text';
  warningText.textContent =
    '워드프레스 편집기 서식(글자 크기 · 정렬 · 인용 · 색 등)은 매장 페이지 디자인과 다르게 보일 수 있습니다. 반영한 뒤 매장 사이트에서 모바일 화면으로 확인해 주세요.';

  warningBody.append(warningTitle, warningText);
  warning.append(warningIcon, warningBody);

  // 편집기를 받는 중 · 못 받았을 때 안내 — Pro 칸 아래
  const status = document.createElement('p');
  status.className = 'mode-editor__status';
  status.setAttribute('role', 'status');
  status.hidden = true;

  element.append(simple.element, proHolder, status, warning);

  // ── 모드 전환 버튼 ─────────────────────────────────────────────
  const switcher = document.createElement('div');
  switcher.className = 'segmented';
  switcher.setAttribute('role', 'group');
  switcher.setAttribute('aria-label', '편집 모드');

  const option = (value, label, title) => {
    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'segmented__option';
    button.dataset.mode = value;
    button.title = title;
    button.textContent = label;
    switcher.append(button);

    return button;
  };

  const simpleButton = option(MODE_SIMPLE, 'Simple', '매장 페이지 서식만 쓰는 기본 편집기');
  const proButton = option(MODE_PRO, 'Pro', '워드프레스 편집기로 자유롭게 편집');

  // ── Pro 편집기 ────────────────────────────────────────────────
  const proTextarea = () => document.getElementById(id);

  const readPro = () => {
    if (proStarted) {
      return window.wp.editor.getContent(id) || '';
    }

    return proTextarea()?.value ?? pendingProHTML;
  };

  const setStatus = (text = '', state = '') => {
    status.textContent = text;
    status.dataset.state = state;
    status.hidden = '' === text;
  };

  /**
   * Pro 칸을 연다. 편집기 스크립트가 아직 없으면 먼저 받는다 — 그동안은 HTML 을 그대로 고치는 textarea 가 보이고,
   * 못 받으면 그 칸을 그대로 둔다 (내용은 잃지 않는다).
   */
  const startPro = (content) => {
    proHolder.textContent = '';
    proAttempt += 1;
    const attempt = proAttempt;

    const textarea = document.createElement('textarea');
    textarea.id = id;
    textarea.className = 'mode-editor__textarea';
    textarea.rows = 14;
    textarea.value = content;
    proHolder.append(textarea);

    if (proReady()) {
      initPro(textarea);
      return;
    }

    setStatus('Pro 편집기를 불러오는 중입니다...', 'loading');

    loadProEditor()
      .then((ready) => {
        if (attempt !== proAttempt || proTextarea() !== textarea) {
          return;
        }

        setStatus();

        if (ready) {
          initPro(textarea);
        } else {
          setStatus('Pro 편집기를 쓸 수 없습니다. HTML 을 직접 고칠 수 있습니다.', 'error');
        }
      })
      .catch((error) => {
        console.error(error);

        if (attempt === proAttempt && proTextarea() === textarea) {
          setStatus(
            'Pro 편집기를 불러오지 못했습니다. 새로고침해 주세요. HTML 은 직접 고칠 수 있습니다.',
            'error'
          );
        }
      });
  };

  /** 문서에 붙은 textarea 위에 워드프레스 편집기를 띄운다 (스크립트가 있을 때만) */
  const initPro = (textarea) => {
    const dialog = element.closest('dialog');
    const tinymce = {
      // 문단(<p>)을 그대로 두어 코드 탭에서도 저장될 HTML 이 보이게 한다
      wpautop: false,
      // 전체 화면은 페이지에서만 — 모달 안에서는 스크롤 영역에 갇혀 잘린다
      toolbar1: dialog ? PRO_TOOLBAR1 : `${PRO_TOOLBAR1},fullscreen`,
      toolbar2: PRO_TOOLBAR2,
      height: dialog ? 260 : 320,
      content_style: PRO_CONTENT_STYLE,
      /**
       * 링크는 워드프레스 링크 창(wpLink)으로 연다. 기본 동작은 글 옆에 뜨는 작은 링크 도구줄인데
       * body 에 붙어 모달(<dialog>) 뒤에 가려지고, 페이지에서도 전역 입력 규칙에 모양이 흐트러진다.
       * 링크를 누르면 뜨는 미리보기 도구줄도 같은 까닭으로 끈다. 플러그인이 붙은 뒤(init)에 덮어써야 이긴다.
       */
      setup: (editor) => {
        editor.on('init', () => {
          editor.addCommand('WP_Link', () => window.wpLink?.open(editor.id));
          editor.on('wptoolbar', (event) => {
            event.toolbar = false;
          });
        });
      },
    };

    if (dialog) {
      if (!dialog.id) {
        dialog.id = `${id}-dialog`;
      }

      tinymce.ui_container = `#${dialog.id}`;
    }

    keepLinkDialogInModal();

    window.wp.editor.initialize(textarea.id, { tinymce, quicktags: true, mediaButtons: false });
    proStarted = true;
  };

  const stopPro = () => {
    if (proStarted) {
      window.wp.editor.remove(id);
    }

    proAttempt += 1;
    proStarted = false;
    proHolder.textContent = '';
    setStatus();
  };

  const sync = () => {
    const pro = MODE_PRO === current;

    simple.element.hidden = pro;
    proHolder.hidden = !pro;
    warning.hidden = !pro;
    simpleButton.setAttribute('aria-pressed', pro ? 'false' : 'true');
    proButton.setAttribute('aria-pressed', pro ? 'true' : 'false');
    element.dataset.mode = current;
  };

  const switchTo = (next) => {
    if (next === current) {
      return;
    }

    if (MODE_PRO === next) {
      startPro(simple.getHTML());
      current = MODE_PRO;
      sync();
      return;
    }

    const content = readPro();

    if (
      hasProOnlyMarkup(content) &&
      !window.confirm(
        'Simple 로 바꾸면 Simple 에서 쓸 수 없는 서식(정렬 · 인용 · 링크 · 색 등)은 사라지고 글자만 남습니다.\n바꿀까요?'
      )
    ) {
      return;
    }

    simple.setHTML(content);
    stopPro();
    current = MODE_SIMPLE;
    sync();
  };

  function mount() {
    if (MODE_PRO === current && !proStarted && !proTextarea()) {
      startPro(pendingProHTML);
      pendingProHTML = '';
    }
  }

  simpleButton.addEventListener('click', () => switchTo(MODE_SIMPLE));
  proButton.addEventListener('click', () => switchTo(MODE_PRO));

  sync();

  return {
    element,
    switcher,
    getMode: () => current,
    getHTML: () => {
      if (MODE_SIMPLE === current) {
        return simple.getHTML();
      }

      const value = readPro().trim();

      return isBlankHTML(value) ? '' : value;
    },
    /** element 를 문서에 넣은 뒤 부른다 — Pro 로 열린 글이면 이때 편집기를 띄운다 */
    mount,
    /**
     * 글과 모드를 통째로 바꾼다 (모달을 다른 글로 열 때). Pro 편집기는 다음 프레임에 띄운다 —
     * 같은 클릭으로 모달이 막 열리는 중이라 보이는 자리에서 띄워야 크기를 제대로 잡는다.
     */
    setContent: (next = '', nextMode = MODE_SIMPLE) => {
      stopPro();
      current = MODE_PRO === nextMode ? MODE_PRO : MODE_SIMPLE;
      simple.setHTML(MODE_SIMPLE === current ? next : '');
      pendingProHTML = MODE_PRO === current ? next : '';
      sync();

      if (MODE_PRO === current) {
        window.requestAnimationFrame(mount);
      }
    },
    /** 설명글 칸에 커서를 둔다 (필수 칸이 비었을 때) */
    focus: () => {
      const editor = proStarted ? window.tinymce?.get(id) : null;

      if (editor && !editor.isHidden()) {
        editor.focus();
        return;
      }

      (proTextarea() && MODE_PRO === current
        ? proTextarea()
        : simple.element.querySelector('[contenteditable]')
      )?.focus();
    },
    destroy: stopPro,
  };
};

/** 모드에 맞춰 미리보기용으로 거른다 */
const sanitizeModeHTML = (html, mode) =>
  MODE_PRO === mode ? sanitizeProHTML(html) : sanitizeHTML(html);

export { createModeEditor, sanitizeModeHTML, MODE_SIMPLE, MODE_PRO };
